import React, { useState } from "react";
import { connect } from "react-redux";
import VerifiedIcon from "@mui/icons-material/Verified";
import PropTypes from "prop-types";
import PreviousVideos from "../../BasicsTraining/PreviousVideos";
import { defaultVideoState } from "../../../helpers/defaultVideoState";
import "./LivestreamPage.scss";

/**
 * A reusable livestream component that can be used across multiple pages
 * Supports live video embedding, author info, schedules, and custom right sidebar content
 */
const LivestreamPage = ({
  // Video configuration
  eventID,
  videoKey,
  titleFilter,

  // Page content
  pageTitle,
  liveTitle,
  pastStreamTitle = "Past Stream",

  // Author information
  authorName,
  authorTitle,
  authorAvatar,

  // Content sections
  contentTitle,
  contentTimestamp,
  contentDescription,

  // Schedule configuration
  showSchedule = false,
  schedules = [],
  scheduleTitle = "Live Schedule",
  scheduleStatus = null,
  scheduleReplayMsg = null,

  // Sidebar component
  sidebarComponent,

  // Custom body component (replaces default body layout)
  customBodyComponent,

  // Previous videos config
  customPageSize = 19,
  showPreviousVideos = true,

  // Theme
  themeMode,

  // Custom className for styling overrides
  className = "",

  // External video state (optional - for sharing state with parent when PreviousVideos is rendered externally)
  currentVideo: externalCurrentVideo,
  setCurrentVideo: externalSetCurrentVideo,
}) => {
  const [internalCurrentVideo, setInternalCurrentVideo] =
    useState(defaultVideoState);
  const [tab, setTab] = useState("1");

  // Use external state if provided, otherwise use internal state
  const currentVideo = externalCurrentVideo ?? internalCurrentVideo;
  const setCurrentVideo = externalSetCurrentVideo ?? setInternalCurrentVideo;

  return (
    <div className={`livestream-page ${className}`}>
      {/* Optional page title */}
      {pageTitle && (
        <div className="livestream-page__page-title">
          {currentVideo.live ? liveTitle : pastStreamTitle}
        </div>
      )}

      {/* Video Section */}
      {currentVideo.live ? (
        <div className="livestream-page__header">
          <iframe
            key={themeMode}
            className="livestream-page__video"
            src={`https://vimeo.com/event/${eventID}/embed/interaction`}
            width="1920"
            height="1080"
            frameBorder="0"
            allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
            title="LiveStream"
            data-theme={themeMode}
          ></iframe>
        </div>
      ) : (
        <div>
          <div
            dangerouslySetInnerHTML={{
              __html: currentVideo.url,
            }}
          />
          <div style={{ position: "relative", top: "5px", left: "29px" }}>
            <div className="row">
              <span
                className="col-12"
                style={{ color: "#A2A3A5", whiteSpace: "nowrap" }}
              >
                {currentVideo.duration} | {currentVideo.date} |{" "}
                {tab === "1" ? "Group Training" : ""}
              </span>
            </div>
            <div className="row">
              <span className="col-12" style={{ paddingTop: "10px" }}>
                {currentVideo.description}
              </span>
            </div>
          </div>
          <br />
          <div
            style={{
              width: "99%",
              border: "1px solid #F1F1F1",
              marginLeft: "24px",
            }}
          ></div>
          <br />
        </div>
      )}

      {/* Body Section: Main content and sidebar */}
      {customBodyComponent ? (
        customBodyComponent
      ) : (
        <div className="livestream-page__body">
          <div className="livestream-page__left">
            <div className="livestream-page__content">
              {/* Author Section */}
              {authorName && (
                <div className="livestream-page-author">
                  <img
                    src={authorAvatar}
                    alt={`${authorName} Avatar`}
                    className="livestream-page-author-avatar"
                  />
                  <div className="livestream-page-author-info">
                    <div className="livestream-page-author-name">
                      {authorName} <VerifiedIcon />
                    </div>
                    <div className="livestream-page-author-title">
                      {authorTitle}
                    </div>
                  </div>
                </div>
              )}

              {/* Content Information */}
              <div className="livestream-page__info">
                {contentTitle && (
                  <div className="livestream-page__content-title">
                    {contentTitle}
                  </div>
                )}
                {contentTimestamp && (
                  <div className="livestream-page__content-timestamp">
                    {contentTimestamp}
                  </div>
                )}
                {contentDescription && (
                  <div className="livestream-page__content-detail">
                    {contentDescription}
                  </div>
                )}
              </div>

              {/* Schedule Section */}
              {showSchedule && schedules.length > 0 && (
                <>
                  <div className="livestream-page__live_schedule-title">
                    {scheduleTitle}
                  </div>
                  <div className="livestream-page__schedules">
                    <div className="flex flex-row justify-between no-margin w-full">
                      {scheduleStatus && (
                        <div className="livestream-page__schedule-status">
                          {scheduleStatus}
                        </div>
                      )}
                      {schedules.map((schedule, index) => (
                        <div className="livestream-page__schedule" key={index}>
                          <div
                            className={`livestream-page__schedule-day ${videoKey === "basic-training" ? "text-left" : ""}`}
                          >
                            {schedule.day}
                          </div>
                          <div className="livestream-page__schedule-time">
                            {schedule.time}
                          </div>
                        </div>
                      ))}
                    </div>
                    {scheduleReplayMsg && (
                      <div className="livestream-page__schedule-replay-msg">
                        {scheduleReplayMsg}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Sidebar Component */}
          {sidebarComponent && (
            <div className="livestream-page__right">{sidebarComponent}</div>
          )}
        </div>
      )}

      {/* Previous Videos Section */}
      {showPreviousVideos && videoKey && (
        <PreviousVideos
          tab={tab}
          setTab={setTab}
          basics={false}
          titleFilter={titleFilter}
          customPageSize={customPageSize}
          currentVideo={currentVideo}
          setCurrentVideo={setCurrentVideo}
          videoKey={videoKey}
        />
      )}
    </div>
  );
};

LivestreamPage.propTypes = {
  // Video configuration
  eventID: PropTypes.string.isRequired,
  videoKey: PropTypes.string,
  titleFilter: PropTypes.string,

  // Page content
  pageTitle: PropTypes.bool,
  liveTitle: PropTypes.string,
  pastStreamTitle: PropTypes.string,

  // Author information
  authorName: PropTypes.string,
  authorTitle: PropTypes.string,
  authorAvatar: PropTypes.string,

  // Content sections
  contentTitle: PropTypes.string,
  contentTimestamp: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  contentDescription: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),

  // Schedule configuration
  showSchedule: PropTypes.bool,
  schedules: PropTypes.arrayOf(
    PropTypes.shape({
      day: PropTypes.string.isRequired,
      time: PropTypes.string.isRequired,
    }),
  ),
  scheduleTitle: PropTypes.string,
  scheduleStatus: PropTypes.string,
  scheduleReplayMsg: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),

  // Sidebar component
  sidebarComponent: PropTypes.node,

  // Custom body component
  customBodyComponent: PropTypes.node,

  // Previous videos config
  customPageSize: PropTypes.number,
  showPreviousVideos: PropTypes.bool,

  // Theme
  themeMode: PropTypes.string,

  // Custom className
  className: PropTypes.string,

  // External video state (for parent-controlled video switching)
  currentVideo: PropTypes.object,
  setCurrentVideo: PropTypes.func,
};

const stateToProps = (state) => ({
  themeMode: state.themeMode.themeMode,
});

export default connect(stateToProps, null)(LivestreamPage);
